import type { Parchment } from 'quill/core';
import React, { useMemo, useState } from 'react';
import { MenuSeparator } from '../../../components/Menu';
import { useQuill, useQuillReadOnly } from '../../QuillContext';
import EDITOR_INTEGRATION_MENTIONS from '../../mentions/EditorIntegrationMentions';
import getEditorMentionUrl from '../../utils/getEditorMentionUrl';
import matchMention from '../../utils/matchMention';
import replaceEmbedBlotWith from '../../utils/replaceEmbedBlotWith';
import ConvertIntoCardMenuItem from '../ConvertIntoCardMenuItem';
import ConvertIntoEmbedMenuItem from '../ConvertIntoEmbedMenuItem';
import ConvertIntoLinkMenuItem from '../ConvertIntoLinkMenuItem';
import CopyLinkMenuItem from '../CopyLinkMenuItem';
import OpenInNewTabMenuItem from '../OpenInNewTabMenuItem';
import { editLinkIcon } from '../editorIcons';
import DeleteMenuItem from './DeleteMenuItem';
import LinkEditor from './LinkEditor';
import Toolbar, { ToolbarOverflowMenu } from './Toolbar';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';

interface MentionToolbarOverflowMenuItemsProps {
  blot: Parchment.EmbedBlot;
  link: string;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const MentionToolbarOverflowMenuItems = ({
  blot,
  link,
}: MentionToolbarOverflowMenuItemsProps) => {
  const { quill } = useQuill();
  const { type } = blot.statics.value(blot.domNode);

  const canBeRenderedAsEmbed = useMemo(() => {
    return (
      quill?.scroll.query('embed') &&
      (type === 'post' ||
        EDITOR_INTEGRATION_MENTIONS[type]?.modes.includes('embed'))
    );
  }, [type, quill]);

  const canBeRenderedAsCard = useMemo(() => {
    return type === 'post' && quill?.scroll.query('card');
  }, [type, quill]);

  if (!quill) return null;

  return (
    <>
      <OpenInNewTabMenuItem link={link} />
      {canBeRenderedAsCard && (
        <ConvertIntoCardMenuItem quill={quill} blot={blot} />
      )}
      {canBeRenderedAsEmbed && (
        <ConvertIntoEmbedMenuItem quill={quill} blot={blot} />
      )}
      <ConvertIntoLinkMenuItem quill={quill} blot={blot} link={link} />
      <CopyLinkMenuItem link={link} />
      <MenuSeparator />
      <DeleteMenuItem onClick={() => blot.remove()}>
        Remove mention
      </DeleteMenuItem>
    </>
  );
};

interface MentionToolbarProps {
  blot: Parchment.EmbedBlot;
  initialEditingTarget?: 'link' | 'text';
}

const MentionToolbar = ({
  blot,
  initialEditingTarget,
}: MentionToolbarProps) => {
  const { quill } = useQuill();
  const readOnly = useQuillReadOnly();
  const [initialText, setInitialText] = useState<string | null>(null);
  const [textPlaceholder, setTextPlaceholder] = useState<string | null>(null);
  const [editingTarget, setEditingTarget] = useState<null | 'link' | 'text'>(
    initialEditingTarget || null,
  );

  const link = useMemo(() => {
    const { id, params, type } = blot.statics.value(blot.domNode);
    const formats = blot.statics.formats(blot.domNode) || {};
    return getEditorMentionUrl({ id, type, params, content: formats.content });
  }, [blot]);

  if (readOnly) return null;

  if (editingTarget) {
    return (
      <LinkEditor
        blot={blot}
        initialLink={link || ''}
        initialText={initialText || ''}
        textPlaceholder={textPlaceholder || ''}
        editingTarget={editingTarget}
        onEditingTargetChange={setEditingTarget}
        onSave={(value) => {
          if (!quill) return;

          if (editingTarget === 'link') {
            const mention = matchMention(value);
            if (mention) {
              replaceEmbedBlotWith(quill, blot, { mention: mention.value });
            } else {
              replaceEmbedBlotWith(quill, blot, value, { link: value });
            }
          } else if (editingTarget === 'text') {
            if (
              !value ||
              value === blot.statics.formats(blot.domNode)?.content
            ) {
              blot.format('mention-text', null);
            } else {
              blot.format('mention-text', value);
            }
          }
        }}
      />
    );
  }

  return (
    <Toolbar
      hideEditButton
      overflowMenu={
        link ? (
          <ToolbarOverflowMenu>
            <MentionToolbarOverflowMenuItems blot={blot} link={link} />
          </ToolbarOverflowMenu>
        ) : undefined
      }
    >
      <ToolbarButtonGroup>
        <ToolbarButton
          icon={editLinkIcon}
          tooltip="Edit mention"
          onClick={() => {
            const formats = blot.statics.formats(blot.domNode) || {};
            setInitialText(formats['mention-text'] || formats.content);
            setTextPlaceholder(formats.content);
            setEditingTarget('link');
          }}
          testId="mentionToolbarEditButton"
        />
      </ToolbarButtonGroup>
      <ToolbarCommentButtonGroup blot={blot} />
    </Toolbar>
  );
};

export default MentionToolbar;
